// assets/AllCourses/Exercises/MissingAlphabetsExercise.jsx

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { StringField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

export default function MissingAlphabetsExercise({
  exercise = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResults = false,
  onComplete = () => {},
  setAttempt = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise.missingAlphabetsQuestions)
    ? exercise.missingAlphabetsQuestions
    : [];

  /* ================= STATE ================= */
  const [state, setState] = useState({
    currentIndex: 0,
    answers: [],
    hasInteracted: false,
  });

  const completedRef = useRef(false);

  /* ================= RESET ON RETRY ================= */
  useEffect(() => {
    if (!exercise?._id) return;

    const initialAnswers = questions.map(q =>
      Array((q.word.match(/_+/g) || []).length).fill("")
    );

    setState({
      currentIndex: 0,
      answers: initialAnswers,
      hasInteracted: false,
    });

    completedRef.current = false;
  }, [exercise._id]);

  /* ================= RESET COMPLETE FLAG ================= */
  useEffect(() => {
    if (!showResults) completedRef.current = false;
  }, [showResults]);

  /* ================= NORMALIZE ================= */
  const normalize = (text = "", requireCap = false) => {
    let t = String(text).trim();
    if (!requireCap) t = t.toLowerCase();
    return t.replace(/[.,!?]/g, "");
  };

  /* ================= EDIT MODE ================= */
  const updateQuestions = next => {
    if (isEditMode) onUpdate("missingAlphabetsQuestions", next);
  };

  const updateWord = (idx, value) => {
    if (!isEditMode) return;

    const next = [...questions];
    const blanks = (value.match(/_+/g) || []).length;
    const prevAnswers = next[idx]?.answer || [];

    next[idx] = {
      ...next[idx],
      word: value,
      answer: Array.from({ length: blanks }, (_, i) => prevAnswers[i] || ""),
    };

    updateQuestions(next);
  };

  /* ================= INPUT ================= */
  const handleInputChange = (blankIdx, value) => {
    if (isEditMode || showResults) return;

    setState(prev => {
      const next = structuredClone(prev);

      next.answers[next.currentIndex][blankIdx] = value;
      next.hasInteracted = true;

      setAttempt(next);

      // 🔥 ALWAYS notify parent (important for submit button)
      onComplete(next.answers, false);

      const allFilled = next.answers.every(a =>
        a.every(v => v.trim() !== "")
      );

      if (allFilled && !completedRef.current) {
        completedRef.current = true;
        checkAnswers(next);
      }

      return next;
    });
  };

  /* ================= NEXT ================= */
  const nextQuestion = () => {
    setState(prev => {
      const next = { ...prev, currentIndex: prev.currentIndex + 1 };
      setAttempt(next);
      return next;
    });
  };

  /* ================= CHECK ================= */
  const checkAnswers = currentState => {
    const allCorrect = questions.every((q, qIdx) => {
      const blanks = (q.word.match(/_+/g) || []).length;

      return Array.from({ length: blanks }).every((_, aIdx) => {
        const user = currentState.answers[qIdx]?.[aIdx] || "";
        const correct = q.answer[aIdx] || "";

        return (
          normalize(user, q.requireCapitalization) ===
          normalize(correct, q.requireCapitalization)
        );
      });
    });

    // ✅ CORRECT FORMAT
    onComplete(currentState.answers, allCorrect);
  };

  /* ================= EDIT MODE UI ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.map((q, idx) => {
          const blanks = (q.word.match(/_+/g) || []).length;

          return (
            <Card key={idx} className="p-6">
              <div className="space-y-6">
                <StringField
                  label={`Word ${idx + 1} (use _ for blanks)`}
                  value={q.word || ""}
                  onChange={v => updateWord(idx, v)}
                  placeholder="A__le"
                  required
                />

                <div className="space-y-3">
                  <p className="font-medium">Letters</p>

                  {Array.from({ length: blanks }).map((_, aIdx) => (
                    <StringField
                      key={aIdx}
                      label={`Letter ${aIdx + 1}`}
                      value={q.answer[aIdx] || ""}
                      onChange={v => {
                        const next = [...questions];
                        next[idx].answer[aIdx] = v;
                        updateQuestions(next);
                      }}
                      maxLength={1}
                    />
                  ))}
                </div>

                <CapitalizationRule
                  checked={!!q.requireCapitalization}
                  onChange={c => {
                    const next = [...questions];
                    next[idx].requireCapitalization = c;
                    updateQuestions(next);
                  }}
                />

                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() =>
                    updateQuestions(questions.filter((_, i) => i !== idx))
                  }
                >
                  Remove
                </Button>
              </div>
            </Card>
          );
        })}

        <Button
          variant="outline"
          onClick={() =>
            updateQuestions([
              ...questions,
              { word: "", answer: [], requireCapitalization: false },
            ])
          }
        >
          Add Word
        </Button>
      </div>
    );
  }

  /* ================= STUDENT ================= */
  const current = questions[state.currentIndex];

  if (!current) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>No questions available.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const parts = current.word.split(/_+/g);
  let blankIdx = 0;

  return (
    <Card className="max-w-3xl mx-auto shadow-sm">
      <CardHeader>
        <CardTitle>Missing Letters</CardTitle>
        <CardDescription>
          Question {state.currentIndex + 1} of {questions.length}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-8">
        <div className="rounded-xl border bg-muted/40 p-8 text-center">
          <div className="flex flex-wrap justify-center items-center gap-3 text-4xl font-bold">
            {parts.map((part, i) => {
              if (i === parts.length - 1) return <span key={i}>{part}</span>;

              const idx = blankIdx++;
              const user = state.answers[state.currentIndex]?.[idx] || "";
              const correct = current.answer[idx] || "";

              const isCorrect =
                user &&
                normalize(user, current.requireCapitalization) ===
                  normalize(correct, current.requireCapitalization);

              return (
                <React.Fragment key={i}>
                  <span>{part}</span>
                  <Input
                    maxLength={1}
                    value={user}
                    onChange={e => handleInputChange(idx, e.target.value)}
                    disabled={showResults}
                    className={cn(
                      "w-14 h-14 text-center text-3xl font-bold border",

                      showResults && isCorrect && "bg-green-100 border-green-400",
                      showResults && user && !isCorrect && "bg-red-100 border-red-400"
                    )}
                  />
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {!showResults && state.currentIndex < questions.length - 1 && (
          <div className="text-center">
            <Button
              onClick={nextQuestion}
              disabled={state.answers[state.currentIndex]?.some(a => !a.trim())}
            >
              Next →
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}